import { useEffect, useCallback } from 'react';
import { driver, Driver, DriveStep } from 'driver.js';

import 'driver.js/dist/driver.css';
import './driverstyle.css';

interface ApprovalsTourProps {
  isOpen: boolean;
  onClose: () => void;
}

const ApprovalsTour = ({ isOpen, onClose }: ApprovalsTourProps) => {
  const startTour = useCallback(() => {
    const steps: DriveStep[] = [
      {
        popover: {
          title: '1.Добре дошли в Vion HR',
          description: 'Сега ще ви покажем как можете да използвате Vion HR.',
          side: 'bottom',
          align: 'center',
          popoverClass: 'driverjs-theme',
        },
      },
      {
        element: '#calendar-days',
        popover: {
          title: '2.Изберете дни',
          description:
            'Кликнете върху датите, за които искате да подадете искане за отсъствия.',
          side: 'bottom',
          align: 'center',
          popoverClass: 'driverjs-theme',
        },
      },
      {
        element: '#selectDays',
        popover: {
          title: '3.Избери вид отсъствиe',
          description:
            'След като сме избрали дните трябва да изберем вида отсъствие.',
          side: 'bottom',
          align: 'center',
          popoverClass: 'driverjs-theme',
        },
      },
      {
        element: '#absence-table',
        popover: {
          title: '4.Преглед на отсъствия',
          description: `Обобщение на отсъствията, които сте избрали.
          Следните статуси означават:
          <div style="display: flex; align-items: center; margin: 8px 0;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="gray" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text" style="margin-right: 8px; flex-shrink: 0;"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
            <span>Не е изпратено oще и трябва да се изпрати.</span>
          </div>
          <div style="display: flex; align-items: center; margin: 8px 0;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="orange" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock" style="margin-right: 8px; flex-shrink: 0;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>Изпратено и очаква одобрение.</span>
          </div>
          <div style="display: flex; align-items: center; margin: 8px 0;">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="green" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check-big" style="margin-right: 8px; flex-shrink: 0;"><path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/></svg>
            <span>Изпратено и е одобрено.</span>
          </div>`,
          side: 'right',
          align: 'start',
          popoverClass: 'driverjs-theme',
        },
      },
      {
        element: '#send-button',
        popover: {
          title: '5.Изпрати за одобрение',
          description:
            'След като сте избрали отсъствията, можете да изпратите искането за одобрение.',
          side: 'left',
          align: 'center',
          popoverClass: 'driverjs-theme',
        },
      },
      {
        element: '#info-button',
        popover: {
          title: '6.Благодаря за вниманието',
          description: 'За да видите отново това обучение, кликнете тук.',
          side: 'left',
          align: 'center',
          popoverClass: 'driverjs-theme',
        },
      },
    ];

    let selectChangeListener: () => void;

    const driverObj: Driver = driver({
      showProgress: true,
      allowClose: false,
      disableActiveInteraction: true,
      steps,
      nextBtnText: 'Напред', // Bulgarian for "Next"
      prevBtnText: 'Назад', // Bulgarian for "Previous"
      doneBtnText: 'Готово', // Bulgarian for "Done"
      progressText: '{{current}} от {{total}}',
      onDestroyed: () => {
        console.log('Tour completed');
        onClose();
        if (selectChangeListener) {
          document.removeEventListener('change', selectChangeListener);
        }
      },
      onHighlightStarted: (element) => {
        if (element?.id === 'selectDays') {
          const selectButton = element.querySelector('button') as HTMLElement;
          if (selectButton) {
            selectButton.click();
          }
        }

        if (element?.classList.contains('SelectTrigger')) {
          const trigger = element as HTMLElement;
          if (trigger) {
            trigger.click();
          }
        }

        if (element?.classList.contains('SelectContent')) {
          const select = document.querySelector('select');
          if (select) {
            selectChangeListener = () => {
              setTimeout(() => {
                driverObj.moveNext();
              }, 500);
            };
            select.addEventListener('change', selectChangeListener);
          }
        }
      },
    });

    driverObj.drive();

    return () => {
      driverObj.destroy();
      if (selectChangeListener) {
        document.removeEventListener('change', selectChangeListener);
      }
    };
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      startTour();
    }
  }, [isOpen, startTour]);

  return null;
};

export default ApprovalsTour;
